/**
 * AW Collection Filters
 * Plain-class controller for drawer-based filter + sort.
 * Same pattern as AwQuickAddController — no web components.
 *
 * Root:        [data-aw-filters]
 * Form:        form inside the root
 * Price range: [data-aw-price-range] inside the form
 * Remove link: [data-aw-filter-remove] anywhere on the page
 * Apply btn:   [data-aw-filter-apply]
 */

(function () {
  /* ── Price range pair ───────────────────────────────────── */

  class AwPriceRange {
    constructor(el) {
      const inputs = el.querySelectorAll('input');
      this.minInput = inputs[0];
      this.maxInput = inputs[1];
      if (!this.minInput || !this.maxInput) return;

      inputs.forEach(input => input.addEventListener('change', this.onRangeChange.bind(this)));
      this.syncMinMax();
    }

    onRangeChange(event) {
      this.adjustToValidValues(event.currentTarget);
      this.syncMinMax();
    }

    syncMinMax() {
      const { minInput: min, maxInput: max } = this;
      if (max.value) min.setAttribute('max', max.value);
      if (min.value) max.setAttribute('min', min.value);
      if (min.value === '') max.setAttribute('min', 0);
      if (max.value === '') min.setAttribute('max', min.getAttribute('max'));
    }

    adjustToValidValues(input) {
      const value = Number(input.value);
      const min   = Number(input.getAttribute('min'));
      const max   = Number(input.getAttribute('max'));
      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }
  }

  /* ── Filter controller ──────────────────────────────────── */

  class AwCollectionFiltersController {
    constructor(el) {
      this.el   = el;
      this.form = el.querySelector('form');
      this.filterData = [];

      if (!this.form) return;

      window.addEventListener('popstate', this.onHistoryChange.bind(this));

      // Apply filters on "View Products" click
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-aw-filter-apply]')) {
          const searchParams = new URLSearchParams(new FormData(this.form)).toString();
          this.renderPage(searchParams);
        }
      });

      // Active-filter removal (event delegation)
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-aw-filter-remove]');
        if (!link) return;
        e.preventDefault();
        this.renderPage(new URL(link.href).searchParams.toString());
      });

      this.initPriceRanges();
    }

    initPriceRanges(root) {
      const rootEl = root || this.el;
      rootEl.querySelectorAll('[data-aw-price-range]').forEach(el => {
        if (el.dataset.awPriceRangeInit) return;
        el.dataset.awPriceRangeInit = '1';
        new AwPriceRange(el);
      });
    }

    onHistoryChange(event) {
      const searchParams = (event.state && event.state.searchParams) ? event.state.searchParams : '';
      this.renderPage(searchParams, false);
    }

    renderPage(searchParams, updateURLHash) {
      if (updateURLHash === undefined) updateURLHash = true;

      const gridEl = document.getElementById('main-collection-product-grid');
      if (!gridEl) return;

      const url = `${window.location.pathname}?section_id=${gridEl.dataset.id}&${searchParams}`;

      const productGridEl = document.getElementById('CollectionProductGrid');
      const countEl       = document.getElementById('CollectionProductCountDesktop');
      if (productGridEl) productGridEl.classList.add('loading');
      if (countEl) countEl.classList.add('loading');

      const cached = this.filterData.find(e => e.url === url);
      if (cached) {
        this.renderFromHtml(cached.html);
      } else {
        fetch(url)
          .then(r => r.text())
          .then(html => {
            this.filterData = [...this.filterData, { html, url }];
            this.renderFromHtml(html);
          });
      }

      if (updateURLHash) {
        history.pushState(
          { searchParams },
          '',
          `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`
        );
      }
    }

    renderFromHtml(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      this.renderProductGrid(parsed);
      this.renderCount(parsed);
      this.renderActiveFacets(parsed);
      this.renderFilters(parsed);
      this.initPriceRanges();
      if (window.AW && window.AW.collapse) window.AW.collapse.initAll();
      document.dispatchEvent(new CustomEvent('aw:collection-rendered'));
    }

    renderProductGrid(parsed) {
      const newGrid     = parsed.getElementById('CollectionProductGrid');
      const currentGrid = document.getElementById('CollectionProductGrid');
      if (newGrid && currentGrid) currentGrid.innerHTML = newGrid.innerHTML;
      if (currentGrid) currentGrid.classList.remove('loading');
    }

    renderCount(parsed) {
      const newCount = parsed.getElementById('CollectionProductCountDesktop');
      const countEl  = document.getElementById('CollectionProductCountDesktop');
      if (newCount && countEl) countEl.innerHTML = newCount.innerHTML;
      if (countEl) countEl.classList.remove('loading');
    }

    renderActiveFacets(parsed) {
      const newFacets     = parsed.querySelector('.aw-coll-active-filters');
      const currentFacets = document.querySelector('.aw-coll-active-filters');
      const productGrid   = document.getElementById('CollectionProductGrid');

      if (newFacets && currentFacets) {
        currentFacets.outerHTML = newFacets.outerHTML;
      } else if (newFacets && !currentFacets && productGrid) {
        productGrid.insertAdjacentHTML('beforebegin', newFacets.outerHTML);
      } else if (!newFacets && currentFacets) {
        currentFacets.remove();
      }

      // Sync toolbar badge count
      const pillCount = newFacets ? newFacets.querySelectorAll('.aw-coll-active-filters__pill').length : 0;
      const badge = document.querySelector('[data-aw-drawer-trigger="CollectionFilterDrawer"] .aw-coll-toolbar__badge');
      if (badge) {
        badge.textContent = pillCount;
        badge.hidden = pillCount === 0;
      }
    }

    renderFilters(parsed) {
      parsed.querySelectorAll('.js-filter').forEach(newFilter => {
        const current = document.querySelector('.js-filter[data-index="' + newFilter.dataset.index + '"]');
        if (current) current.innerHTML = newFilter.innerHTML;
      });
    }
  }

  /* ── Init ───────────────────────────────────────────────── */

  function init(root) {
    const rootEl = root || document;
    rootEl.querySelectorAll('[data-aw-filters]').forEach(el => {
      if (el.dataset.awFiltersInit) return;
      el.dataset.awFiltersInit = '1';
      new AwCollectionFiltersController(el);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();